import { Button, Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import {
  ErrorState,
  InlineError,
  LoadingSkeleton,
  Metric,
  NumField,
  SectionCard,
  StickyActionBar,
} from "../../components/ui";
import { api, type StockLevel } from "../../lib/api";
import { Page } from "../../shell/Page";

interface Line {
  productId: string;
  sku: string;
  name: string;
  quantity: number;
  currentStockQty: number;
}

/**
 * Receiving a delivery. The only operation in the whole app that raises stock.
 *
 * Products are picked from the catalogue rather than typed, and the supplier's
 * document number is captured as free text: it has no entity behind it, so it
 * lives in the ledger note instead of pretending to be a foreign key.
 */
export function StockIn() {
  const [catalogue, setCatalogue] = useState<StockLevel[]>();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("");
  const [quantity, setQuantity] = useState<number | string>("");
  const [lines, setLines] = useState<Line[]>([]);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<{ sku: string; delta: number; balance: number }[]>();

  useEffect(() => {
    void (async () => {
      try {
        const { products } = await api.stockLevels();
        setCatalogue(products);
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not load the catalogue.",
        );
      }
    })();
  }, []);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = (catalogue ?? []).filter(
      (product) => !lines.some((line) => line.productId === product.id),
    );
    if (!needle) return pool.slice(0, 25);
    return pool
      .filter(
        (product) =>
          product.sku.toLowerCase().includes(needle) ||
          product.name.toLowerCase().includes(needle),
      )
      .slice(0, 25);
  }, [catalogue, query, lines]);

  const amount = Number(quantity);
  const canAdd = selected !== "" && Number.isInteger(amount) && amount > 0;

  function addLine() {
    if (!canAdd) return;
    const product = (catalogue ?? []).find((row) => row.id === selected);
    if (!product) return;
    setLines((current) => [
      ...current,
      {
        productId: product.id,
        sku: product.sku,
        name: product.name,
        quantity: amount,
        currentStockQty: product.currentStockQty,
      },
    ]);
    setSelected("");
    setQuantity("");
    setQuery("");
  }

  async function submit() {
    if (lines.length === 0 || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.recordStockIn({
        lineItems: lines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
        })),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setReceipt(
        result.movements.map((movement, index) => ({
          sku: lines[index]?.sku ?? "",
          delta: movement.quantityDelta,
          balance: movement.runningBalanceAfter,
        })),
      );
      setLines([]);
      setReference("");
      setNotes("");
    } catch (caught) {
      // Deliberately keeps the lines: if this failed, the operator should see
      // what they were booking in rather than retype a whole delivery.
      setError(caught instanceof Error ? caught.message : "Could not book that in.");
    } finally {
      setBusy(false);
    }
  }

  const totalUnits = lines.reduce((sum, line) => sum + line.quantity, 0);

  if (receipt) {
    return (
      <Page title="Goods received" subtitle={`${receipt.length} line(s) booked in`}>
        <SectionCard title="Booked in">
          <Stack gap="md">
            <Table.ScrollContainer minWidth={340}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Item</Table.Th>
                    <Table.Th ta="right">Received</Table.Th>
                    <Table.Th ta="right">Now on hand</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {receipt.map((row) => (
                    <Table.Tr key={row.sku}>
                      <Table.Td className="num">{row.sku}</Table.Td>
                      <Table.Td ta="right" className="num">
                        +{row.delta}
                      </Table.Td>
                      <Table.Td ta="right" className="num" fw={700}>
                        {row.balance}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Text fz="sm" c="gray.6">
              Each line is now a permanent entry in the movement ledger and
              cannot be edited — a mistake is corrected by a further entry.
            </Text>
            <Button size="md" onClick={() => setReceipt(undefined)}>
              Book in another delivery
            </Button>
          </Stack>
        </SectionCard>
      </Page>
    );
  }

  /**
   * Booking in is the only thing in this app that RAISES stock, and the
   * ledger is append-only, so it cannot be undone - only corrected, which
   * leaves both entries visible. Worth stating before it happens rather than
   * after.
   */
  function confirmSubmit() {
    modals.openConfirmModal({
      title: `Book in ${totalUnits} units`,
      children: (
        <Stack gap="xs">
          <Text fz="md">
            {lines.length} line(s), {totalUnits} units onto the shelf.
          </Text>
          <Text fz="md" c="gray.7">
            This writes one permanent ledger entry per line. It cannot be
            undone — a mistake is fixed with a correction, which keeps both
            records.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Book it in", cancel: "Not yet" },
      onConfirm: () => void submit(),
    });
  }

  return (
    <Page title="Receive goods" subtitle="Book a delivery onto the shelf" back="/inventory">
      {error && !catalogue && <ErrorState message={error} />}
      {error && catalogue && <InlineError>{error}</InlineError>}
      {!catalogue && !error && <LoadingSkeleton variant="card" count={2} />}

      {catalogue && (
        <SectionCard title="Add a line">
          <Stack gap="sm">
            <TextInput
              id="stockin-search"
              label="Product"
              type="search"
              value={query}
              placeholder="SKU or name"
              leftSection={<IconSearch size={16} stroke={1.8} />}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <Select
              id="stockin-product"
              aria-label="Matching products"
              placeholder="Select a product…"
              value={selected || null}
              onChange={(value) => setSelected(value ?? "")}
              searchable
              data={matches.map((product) => ({
                value: product.id,
                label: `${product.sku} — ${product.name} (${product.currentStockQty} on hand)`,
              }))}
            />

            <Group gap="sm" align="flex-end" wrap="nowrap">
              <NumField
                id="stockin-qty"
                label="How many arrived"
                min={1}
                step={1}
                allowDecimal={false}
                value={quantity}
                style={{ flex: 1 }}
                onChange={setQuantity}
                onKeyDown={(event) => {
                  if (event.key === "Enter") addLine();
                }}
                error={
                  selected !== "" && !canAdd ? "Enter a quantity of 1 or more." : undefined
                }
              />
              <Button variant="default" disabled={!canAdd} onClick={addLine}>
                Add
              </Button>
            </Group>
          </Stack>
        </SectionCard>
      )}

      {lines.length > 0 && (
        <SectionCard title="This delivery">
          <Stack gap="sm">
            {lines.map((line) => (
              <Group
                key={line.productId}
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
                gap="sm"
              >
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fz="md" fw={600} c="gray.9">
                    {line.name}
                  </Text>
                  <Text fz="sm" c="gray.6" className="num">
                    {line.sku} · {line.currentStockQty} →{" "}
                    {line.currentStockQty + line.quantity}
                  </Text>
                </Stack>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Metric value={`+${line.quantity}`} size="inline" />
                  <Button
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`Remove ${line.name}`}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((row) => row.productId !== line.productId),
                      )
                    }
                  >
                    Remove
                  </Button>
                </Group>
              </Group>
            ))}

            <Group
              justify="space-between"
              pt="xs"
              style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
            >
              <Text fz="md" c="gray.7">
                {lines.length} line(s)
              </Text>
              <Text fz="lg" fw={700} c="gray.9" className="num">
                {totalUnits} units
              </Text>
            </Group>

            <TextInput
              id="stockin-ref"
              label="Supplier invoice or delivery note"
              maxLength={100}
              placeholder="Optional, but makes a receipt traceable"
              value={reference}
              onChange={(event) => setReference(event.currentTarget.value)}
            />
            <TextInput
              id="stockin-notes"
              label="Note"
              description="Optional."
              maxLength={500}
              value={notes}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />
          </Stack>
        </SectionCard>
      )}

      {lines.length > 0 && (
        <StickyActionBar
          context={`${lines.length} line(s)`}
          primary={
            <Button size="md" fullWidth loading={busy} onClick={confirmSubmit}>
              Book in {totalUnits} units
            </Button>
          }
        />
      )}
    </Page>
  );
}
